import React from 'react';
import { useServiceTranslation } from '@/hooks/useServiceTranslation';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import dayjs, { locale } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import relativeTime from 'dayjs/plugin/relativeTime';
import duration from 'dayjs/plugin/duration';
import { useChartResizeObserver } from '@/hooks/useChartResizeObserver';
import { IMdResident } from '@/types/api/medical/getResidents';
import { DfreeGraphViewType } from '@/constants';
import { isValueView } from '@/utils/dfreeGraph';

require('highcharts/modules/accessibility')(Highcharts);
dayjs.extend(utc);
dayjs.extend(relativeTime);
dayjs.extend(duration);

interface Props {
  resident: IMdResident;
  dateRange: { start: string; end: string };
  viewType: DfreeGraphViewType;
}

export const DfreeGraph = React.memo(({ resident, dateRange, viewType }: Props) => {
  const {
    bigBladderNotificationSetting,
    sensorReactions,
    bladderThresholdPercentage,
    sensorNotifications,
    careRecordPlots,
  } = resident;
  const chartRef = React.useRef<HighchartsReact.RefObject>(null);
  useChartResizeObserver(chartRef);
  const { t, i18n, serviceT } = useServiceTranslation();
  locale(i18n.language);

  /**
   * たまり具合/尿量のデータ
   * グラフ側でデータが破壊的に変更される可能性があるため、
   * 元の sensorReactions を直接渡さず、新しい配列として生成する
   */
  const sensorReactionsData: [number, number | null][] = React.useMemo(() => {
    const src = isValueView(viewType) ? sensorReactions?.values : sensorReactions?.volumes;
    return (src ?? []).map((p) => [p[0], p[1]] as [number, number | null]);
  }, [sensorReactions, viewType]);

  const residualUrineData = React.useMemo(() => {
    const key = isValueView(viewType) ? 'accumulation' : 'ruv';
    const label = isValueView(viewType)
      ? t('UrineAccumulation', '尿のたまり具合')
      : t('residualUrineVolume', '残尿量');
    const data: { x: number; y: number; color: string; name: string }[] = [];

    (careRecordPlots ?? []).forEach((record) => {
      const value = record[key];
      if (typeof value !== 'number' || !Number.isFinite(value)) return;

      data.push({
        x: Date.parse(record.timestamp),
        y: value,
        color: '#2C8ED9',
        name: `${label}: ${value}${isValueView(viewType) ? '' : 'ml'}`,
      });
    });

    return data;
  }, [careRecordPlots, t, viewType]);

  /**
   * グラフ縦軸の最大値
   */
  const yAxisMaxValue = React.useMemo(() => {
    // たまり具合の場合は10固定
    if (isValueView(viewType)) {
      return 10;
    }
    // 尿量の場合は200刻みに最大値をセット
    else {
      const unit: number = 200;
      const sensorValues: number[] =
        sensorReactions?.volumes.map((d) => d[1]).filter((v): v is number => v !== null) ?? [];
      const values = [...sensorValues, ...residualUrineData.map((point) => point.y)];
      return Math.max(unit, Math.ceil(Math.max(0, ...values) / unit) * unit);
    }
  }, [residualUrineData, sensorReactions, viewType]);

  /**
   * グラフ縦軸の目盛り間隔
   */
  const yTickInterval = React.useMemo(() => (isValueView(viewType) ? 2 : yAxisMaxValue / 2), [yAxisMaxValue, viewType]);

  /**
   * グラフ縦軸の目盛り線
   */
  const getYGrids = React.useCallback(() => {
    let grids: Highcharts.YAxisPlotLinesOptions[] = [];

    const maxValue = isValueView(viewType) ? 10 : yAxisMaxValue;

    for (let i = 0; i < 10; i++) {
      grids = [
        ...grids,
        {
          color: '#CCCCCC',
          dashStyle: 'Solid',
          value: (i * maxValue) / 10,
          width: 1,
        },
      ];
    }
    grids =
      // たまり具合表示の場合のみ、そろそろラインを描画する
      isValueView(viewType)
        ? [
            ...grids,
            {
              color: bigBladderNotificationSetting ? '#FFBB00' : '#C0C0C0',
              dashStyle: 'Solid',
              value: (bladderThresholdPercentage ?? 0) / 10 - 0.03,
              width: 2,
              zIndex: 1,
            },
          ]
        : grids;
    return grids;
  }, [bigBladderNotificationSetting, bladderThresholdPercentage, yAxisMaxValue, viewType]);

  const options: Highcharts.Options = {
    chart: {
      type: 'area',
      zoomType: 'x',
      height: 160,
    },
    time: {
      timezoneOffset: new Date().getTimezoneOffset(),
    },
    title: {
      text: '',
    },
    xAxis: {
      type: 'datetime',
      min: Date.parse(dateRange.start),
      max: Date.parse(dateRange.end),
      showEmpty: true,
      opposite: false,
      gridLineWidth: 1,
      lineWidth: 0,
      tickWidth: 0,
      minPadding: 0,
      maxPadding: 0,
      plotLines: [
        {
          color: '#CCCCCC',
          dashStyle: 'Solid',
          value: Date.parse(dateRange.start),
          width: 0.5,
        },
        {
          color: '#CCCCCC',
          dashStyle: 'Solid',
          value: Date.parse(dateRange.end),
          width: 0.5,
        },
        ...residualUrineData.map((point) => ({
          color: '#2C8ED9',
          dashStyle: 'Solid' as const,
          value: point.x,
          width: 1,
          zIndex: 2,
        })),
      ],
      dateTimeLabelFormats: {
        minute: '%H:%M',
        hour: '%H:00',
        day: t('graphDayFormat', '%m月%d日'),
        week: t('graphDayFormat', '%m月%d日'),
        month: t('graphYearFormat', '%m月%d日'),
      },
      crosshair: {
        width: 2,
      },
    },
    yAxis: {
      title: {
        text: '',
      },
      labels: {
        formatter: function () {
          return this.value as string;
        },
      },
      plotLines: getYGrids(),
      max: yAxisMaxValue,
      min: 0,
      tickInterval: yTickInterval,
      crosshair: {
        width: 2,
      },
    },
    legend: {
      enabled: false,
    },
    plotOptions: {
      area: {
        fillColor: {
          linearGradient: {
            x1: 0,
            y1: 0,
            x2: 0,
            y2: 1,
          },
          stops: [
            [0, 'rgba(123, 214, 231, 0.5)'],
            [1, 'rgba(123, 214, 231, 0.5)'],
          ],
        },
        lineWidth: 3,
        states: {
          hover: {
            lineWidth: 3,
          },
        },
        threshold: null,
      },
      series: {
        marker: {
          enabled: false,
        },
      },
    },
    series: [
      // @ts-ignore
      {
        type: 'area',
        name: isValueView(viewType) ? t('UrineAccumulation', '尿のたまり具合') : t('urineGraphLabel', '尿量'),
        // @ts-ignore
        data: sensorReactionsData,
        color: isValueView(viewType) ? '#7BD6E7' : '#8EC2EC',
        fillColor: isValueView(viewType) ? '#7BD6E780' : '#8EC2EC80',
        borderWidth: 1,
        tooltip: {
          headerFormat: '<b>{series.name}</b><br>',
          pointFormat: isValueView(viewType)
            ? t('graphDateTimeFormatValueView', '{point.name}<br />{point.x:%m月%d日 %H時%M分}')
            : t('graphDateTimeFormatVolumeView', '{point.name}ml<br />{point.x:%m月%d日 %H時%M分}'),
        },
      },
      {
        name: t('notification', '通知'),
        marker: {
          enabled: true,
          symbol: 'circle',
        },
        data: (sensorNotifications ?? [])
          .filter((notification) => {
            const { notificationType } = notification;
            return notificationType === 'urinated' || notificationType === 'bigBladder' || notificationType === 'gotUp';
          })
          .map((notification) => {
            const { notificationType, timestamp } = notification;
            let label = '';
            let color = '';
            if (notificationType === 'urinated') {
              label = t('checkGraphLabel', 'でたかも');
              color = 'rgba(255, 143, 165, 0.8)';
            } else if (notificationType === 'bigBladder') {
              label = t('almostGraphLabel', 'そろそろ');
              color = 'rgba(255, 187, 0, 0.8)';
            } else {
              label = serviceT('gotUpGraphLabel');
              color = 'rgba(255, 50, 35, 0.8)';
            }
            return {
              x: Date.parse(timestamp),
              y: 0,
              name: label,
              color: color,
            };
          }),
        type: 'line', // scatterだとエリア外のマウスオーバーでtooltipがでないためタイプをlineに
        lineWidth: 0,
        states: {
          hover: {
            lineWidth: 0,
            lineWidthPlus: 0,
          },
        },
        tooltip: {
          headerFormat: '<b>{series.name}</b><br>',
          pointFormat: t('graphDateTimePlotFormat', '{point.name}<br />{point.x:%m月%d日 %H時%M分}'),
        },
        stickyTracking: false,
      },
      {
        name: t('careHistory', 'ケア履歴'),
        marker: {
          enabled: true,
          symbol: 'square',
        },
        zIndex: 3,
        data: (careRecordPlots || []).map((careRecordPlots) => {
          const { toilet, pad, timestamp } = careRecordPlots;
          const label = `${t('toilet', 'トイレ')}: ${toilet ? t('yes', '有') : t('no', '無')} ${t(
            'diaper',
            'おむつ/パッド'
          )}: ${pad ? t('yes', '有') : t('no', '無')}`;
          let color;
          if (toilet && !pad) color = '#72fbfd';
          if (toilet && pad) color = '#75f94c';
          if (!toilet && pad) color = '#CCCCCC';
          if (!toilet && !pad) color = '#6D6D6D';
          return {
            x: Date.parse(timestamp),
            y: yAxisMaxValue,
            name: label,
            color,
          };
        }),
        type: 'line', // scatterだとエリア外のマウスオーバーでtooltipがでないためタイプをlineに
        lineWidth: 0,
        states: {
          hover: {
            lineWidth: 0,
            lineWidthPlus: 0,
          },
        },
        tooltip: {
          headerFormat: '<b>{series.name}</b><br>',
          pointFormat: t('graphDateTimePlotFormat', '{point.name}<br />{point.x:%m月%d日 %H時%M分}'),
        },
      },
      {
        name: isValueView(viewType)
          ? t('UrineAccumulation', '尿のたまり具合')
          : t('residualUrineVolume', '残尿量'),
        data: residualUrineData,
        color: '#2C8ED9',
        type: 'line',
        lineWidth: 0,
        states: {
          hover: {
            lineWidth: 0,
            lineWidthPlus: 0,
          },
        },
        marker: {
          enabled: true,
          symbol: 'diamond',
          radius: 6,
        },
        tooltip: {
          headerFormat: '<b>{series.name}</b><br>',
          pointFormat: t('graphDateTimePlotFormat', '{point.name}<br />{point.x:%m月%d日 %H時%M分}'),
        },
        zIndex: 4,
        stickyTracking: false,
      },
    ],
    credits: {
      enabled: false,
    },
  };

  return <HighchartsReact highcharts={Highcharts} options={options} ref={chartRef} />;
});
